/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: tenantsApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API calls for Tenants module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { Tenant } from '../types';

export interface TenantsListResponse {
  success: boolean;
  data: Tenant[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface TenantResponse {
  success: boolean;
  data: Tenant;
  message?: string;
}

export interface ListTenantsParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
}

/**
 * List tenants with pagination
 */
export async function listTenants(
  tenantSlug: string,
  userId: number,
  params: ListTenantsParams
): Promise<TenantsListResponse> {
  const queryParams = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder
  });

  if (params.search?.trim()) {
    queryParams.set('search', params.search.trim());
  }

  const response = await fetch(buildApiUrl(`/api/tenants?${queryParams.toString()}`), {
    headers: getApiHeaders(tenantSlug, userId)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Get tenant by ID
 */
export async function getTenantById(
  tenantSlug: string,
  userId: number,
  id: string
): Promise<TenantResponse> {
  const response = await fetch(buildApiUrl(`/api/tenants/${id}`), {
    headers: getApiHeaders(tenantSlug, userId)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Create tenant
 */
export async function createTenant(
  tenantSlug: string,
  userId: number,
  data: Record<string, unknown>
): Promise<TenantResponse> {
  const response = await fetch(buildApiUrl('/api/tenants'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = errorData?.message || errorData?.error || `HTTP error! status: ${response.status}`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const error = new Error(errorMessage) as any;
    error.status = response.status;
    error.details = errorData?.details;
    error.errorCode = errorData?.error; // Include error code for specific error handling
    throw error;
  }

  return await response.json();
}

/**
 * Update tenant
 */
export async function updateTenant(
  tenantSlug: string,
  userId: number,
  id: string,
  data: Record<string, unknown>
): Promise<TenantResponse> {
  const response = await fetch(buildApiUrl(`/api/tenants/${id}`), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = errorData?.message || errorData?.error || `HTTP error! status: ${response.status}`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const error = new Error(errorMessage) as any;
    error.status = response.status;
    error.details = errorData?.details;
    error.errorCode = errorData?.error;
    throw error;
  }

  return await response.json();
}

/**
 * Delete tenant
 */
export async function deleteTenant(
  tenantSlug: string,
  userId: number,
  id: string
): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(buildApiUrl(`/api/tenants/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders(tenantSlug, userId)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

